import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { WelcomeBackScreen } from "@/components/ui/WelcomeBackScreen";
import { isFileCaptureActive, clearFileCaptureMark } from "@/lib/fileCaptureGate";
import { alLlegarUnAviso, huboAvisoDeArranque } from "@/lib/avisoPendiente";
import { saludoApagadoEnEstaSesion } from "@/lib/saludoDeArranque";

interface SplashGateProps {
  children: ReactNode;
  /** Minimum visible duration of the splash, in ms. */
  minDurationMs?: number;
  /** Fade-out duration, in ms. */
  fadeMs?: number;
}

/**
 * Auth-aware "Welcome back" splash controller.
 *
 * Rules (purely visual — never affects routing, auth, or business data):
 *  - Show on cold app load when the user is already signed in and lands on
 *    an authenticated route (e.g. /dashboard, /worker/today, /perito/*).
 *  - Show right after a successful sign-in (user transitions from null →
 *    authenticated) before they reach the dashboard.
 *  - On native (Despia) and on web, also re-trigger when the app/tab returns
 *    from background to foreground while still signed in (debounced — only
 *    when it was actually hidden for a few seconds).
 *  - Never show on public, auth, OAuth, register, onboarding or welcome routes.
 *  - Wait until profile + company are hydrated so the greeting can show the
 *    company name and the bento can render real numbers.
 *
 * Mounted INSIDE AuthProvider so it can read `useAuth()` directly.
 */

const SPLASH_EXCLUDED_PREFIXES = [
  "/auth",
  "/auth-bridge",
  "/auth-loading",
  "/oauth/",
  "/reset-password",
  "/app-auth",
  "/native-callback",
  "/complete-registration",
  "/payment-success",

  "/trial",
  "/demo",
  "/contact",
  "/privacy",
  "/terms",
  "/cookies",
  "/dpa",
  "/blog",
  "/opiniones",
  "/legacy",
  "/booking",
  "/register",
  "/welcome",
  "/onboarding",
  "/shop/supplier-update",
  "/unirse/",
  // Terminal de la finca: cada PIN es una entrada; el «Bienvenido» no pinta nada ahí.
  "/terminal",
  // NOTE: "/pest" (the landing alias) is deliberately NOT listed — this matcher uses a
  // loose startsWith(), so it would also swallow the in-app "/pests" page and rob it of
  // its cold-start splash.
];

function isSplashExcludedPath(pathname: string): boolean {
  if (pathname === "/") return true;
  return SPLASH_EXCLUDED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p)
  );
}

// Module-level: track whether the cold-start splash has already played in
// this JS session, so internal navigation doesn't keep re-triggering it.
let coldStartConsumed = false;


export function SplashGate({
  children,
  fadeMs = 350,
}: SplashGateProps) {
  // `minDurationMs` is intentionally unused: the splash now waits for the
  // user to tap "Start" rather than auto-dismissing on a timer.
  void 0;
  const location = useLocation();
  const { user, profile, company, role, loading } = useAuth();

  const [showSplash, setShowSplash] = useState(false);
  const [fading, setFading] = useState(false);

  // Track previous user id so we can detect a sign-in transition.
  const prevUserIdRef = useRef<string | null>(null);
  // Track when the page was last hidden so we can detect real foreground
  // returns (not just any visibility flip).
  const lastHiddenAtRef = useRef<number | null>(null);

  const excluded = isSplashExcludedPath(location.pathname);
  // Greeting requires either a company (admin/manager/perito) or, for workers,
  // a profile. Wait until the right piece is hydrated.
  const hydrated =
    !loading &&
    !!user &&
    !!profile &&
    (role === "worker" ? true : !!company);

  // Trigger #1 — cold start while already signed in.
  useEffect(() => {
    if (coldStartConsumed) return;
    if (excluded) return;
    if (!hydrated) return;
    /* QUIEN VIENE TOCANDO UN AVISO NO VIENE A QUE LE SALUDEN (02-09-2026,
       pedido por el usuario). Ha tocado «tarea completada» para ver ESA tarea;
       meter un «Bienvenido» con su botón por delante es una pantalla de más
       entre él y lo que venía a ver. No arregla el enlace —el saludo nunca tocó
       el router, y eso ya se comprobó tres veces— pero sí quita los segundos y
       el toque que sobran en el único camino que importa. */
    coldStartConsumed = true;
    if (huboAvisoDeArranque()) return;
    // Primera vez: aún no ha pasado por la bienvenida de /welcome (o la acaba de ver en esta
    // sesión) → nada de «Bienvenido de nuevo».
    if (profile?.has_seen_welcome === false || saludoApagadoEnEstaSesion()) return;
    setShowSplash(true);
    setFading(false);
  }, [excluded, hydrated, profile?.has_seen_welcome]);

  // Trigger #2 — sign-in transition (user went from null to authenticated
  // during this session, e.g. after submitting the login form).
  useEffect(() => {
    const prev = prevUserIdRef.current;
    const next = user?.id ?? null;
    prevUserIdRef.current = next;

    if (prev === null && next && coldStartConsumed) {
      // We're about to navigate to an authenticated route; show the splash
      // as soon as profile + company are ready.
      // Reset the consumed flag so the hydration effect below shows it.
      coldStartConsumed = false;
    }
  }, [user?.id]);

  // Trigger #3 — return from background (mobile WebView resume / tab refocus).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        lastHiddenAtRef.current = Date.now();
        return;
      }
      // Visible again
      const hiddenAt = lastHiddenAtRef.current;
      lastHiddenAtRef.current = null;
      if (!hiddenAt) return;
      const hiddenMs = Date.now() - hiddenAt;
      // Require at least ~60s in background. Quick tab switches or app
      // hops shorter than a minute should not re-trigger the splash —
      // it interrupts the user's flow without adding value.
      if (hiddenMs < 60000) return;
      if (excluded) return;
      if (!hydrated) return;
      if (profile?.has_seen_welcome === false || saludoApagadoEnEstaSesion()) return;
      // Suppress when the WebView was suspended because of a file/camera
      // picker (taking a photo on iOS), not a real app background.
      if (isFileCaptureActive()) {
        clearFileCaptureMark();
        return;
      }
      // Re-arm and show.
      coldStartConsumed = true;
      setShowSplash(true);
      setFading(false);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [excluded, hydrated, profile?.has_seen_welcome]);

  /* Y SI EL AVISO LLEGA TARDE, se pulsa «Empezar» solo. El envoltorio nativo
     puede entregar el aviso después de que el saludo ya esté puesto —depende de
     lo que tarde el arranque— y entonces el usuario vería el saludo igual. Es la
     misma regla de arriba aplicada un segundo más tarde. */
  useEffect(() => {
    return alLlegarUnAviso(() => {
      setShowSplash((visible) => {
        if (visible) setFading(false);
        return false;
      });
    });
  }, []);

  // No auto-dismiss: the splash now waits for the user to tap "Start".
  const handleStart = () => {
    if (!showSplash) return;
    setFading(true);
    setTimeout(() => {
      setShowSplash(false);
      setFading(false);
    }, fadeMs);
  };

  return (
    <>
      {children}
      {showSplash && (
        <div
          className="fixed inset-0 z-[9999]"
          style={{
            pointerEvents: fading ? "none" : "auto",
            opacity: fading ? 0 : 1,
            transition: `opacity ${fadeMs}ms ease-out`,
          }}
          aria-hidden={fading ? "true" : undefined}
        >
          <WelcomeBackScreen onStart={handleStart} />
        </div>
      )}
    </>
  );
}

export default SplashGate;
